import { getAccessToken } from 'cng-common/helpers/access'

import { promotionsServiceBaseUrl } from '../configs/service'

import type { PromotionsErrorResponseDto } from './dto'

/**
 * Failure of a service-promotions call, carrying what a bug report needs:
 * the HTTP status and the `ReferenceId` that finds the request in service logs.
 */
export class PromotionsServiceError extends Error {
  readonly status: number | 'FETCH_ERROR'
  readonly referenceId?: string

  constructor(message: string, status: number | 'FETCH_ERROR', referenceId?: string) {
    super(message)
    this.name = 'PromotionsServiceError'
    this.status = status
    this.referenceId = referenceId
  }
}

const readErrorEnvelope = async (response: Response) => {
  try {
    const payload = (await response.json()) as PromotionsErrorResponseDto
    const details = (payload.Errors ?? [])
      .map(({ field, message }) => [field, message].filter(Boolean).join(': '))
      .filter(Boolean)
      .join('; ')

    return {
      message: [payload.Message, details].filter(Boolean).join(' — ') || response.statusText,
      referenceId: payload.ReferenceId,
    }
  } catch {
    return { message: response.statusText, referenceId: undefined }
  }
}

interface RequestInitLike {
  readonly method: 'GET' | 'POST' | 'DELETE'
  readonly headers?: Readonly<Record<string, string>>
  readonly body?: string
}

/**
 * The one place a call to the service is made: every request carries the bearer
 * token, and every non-2xx answer becomes a `PromotionsServiceError` rather than
 * a resolved response the caller would have to check itself.
 */
const request = async (path: string, init: RequestInitLike) => {
  let response: Response

  try {
    response = await fetch(`${promotionsServiceBaseUrl}${path}`, {
      method: init.method,
      headers: { ...init.headers, Authorization: `Bearer ${getAccessToken()}` },
      body: init.body,
    })
  } catch (error) {
    throw new PromotionsServiceError(
      error instanceof Error ? error.message : 'Promotions service is unreachable',
      'FETCH_ERROR'
    )
  }

  if (!response.ok) {
    const { message, referenceId } = await readErrorEnvelope(response)

    throw new PromotionsServiceError(message, response.status, referenceId)
  }

  return response
}

const sendJson = (path: string, body: unknown, method: 'POST' | 'DELETE' = 'POST') =>
  request(path, {
    method,
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })

export const postPromotionsJson = async <TResponse>(
  path: string,
  body: unknown
): Promise<TResponse> => {
  const response = await sendJson(path, body)

  return (await response.json()) as TResponse
}

/** For the read endpoints addressed by path, such as `/product-content/{sku}`. */
export const getPromotionsJson = async <TResponse>(path: string): Promise<TResponse> => {
  const response = await request(path, { method: 'GET' })

  return (await response.json()) as TResponse
}

/** For the 204-no-body endpoints. */
export const sendPromotionsCommand = async (
  path: string,
  body: unknown,
  method: 'POST' | 'DELETE'
): Promise<void> => {
  await sendJson(path, body, method)
}
